import { Page, PageBody, PageHeader } from "@/components/app-layout";
import {
  KitDeletionPreviewContent,
  useDeactivationAndDeletionCounts,
} from "@/components/dependency-deletion/KitDeletionDialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuthDetails } from "@/context/auth-context";
import { useKitData, useKitDeletionPreview } from "@/data/use-kits-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { GetKitDto } from "@/lib/gen-api";
import { kitLabel } from "@/lib/kit-label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Separator } from "../../components/ui/separator";

export const DeletePage = () => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const navigate = useNavigate();
  const authDetails = useAuthDetails();
  const { kit, deleteKit, isDeletingKit } = useKitData(groupUuid, kitUuid);
  const { deletionPreview, isLoading } = useKitDeletionPreview(
    groupUuid,
    kitUuid,
  );
  const { countDeletions, countDeactivations } =
    useDeactivationAndDeletionCounts(deletionPreview);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleDelete = async () => {
    if (!kit) return;
    try {
      await deleteKit(kit.uuid);
    } catch {
      toast.error(`Failed to delete ${kitLabel(kit).toLowerCase()}.`);
      return;
    }
    toast.success(`${kitLabel(kit)} ${kit.name} was deleted.`);
    if (kit.type === "project") {
      return navigate({
        to: "/w/$groupSlug/workbooks",
        params: { groupSlug },
        replace: true,
      });
    }
    if (kit.type === "conversation") {
      return navigate({
        to: "/w/$groupSlug/chats",
        params: { groupSlug },
        replace: true,
      });
    }
    if (kit.type === "project_preset") {
      return navigate({
        to: "/w/$groupSlug/settings/presets",
        params: { groupSlug },
        replace: true,
      });
    }
    if (kit.type === "repository") {
      return navigate({
        to: "/w/$groupSlug/settings/knowledge",
        params: { groupSlug },
        replace: true,
      });
    }
    if (kit.type === "expert") {
      return navigate({
        to: "/w/$groupSlug/settings/experts",
        params: { groupSlug },
        replace: true,
      });
    }
    if (kit.type === "skill") {
      return navigate({
        to: "/w/$groupSlug/settings/skills",
        params: { groupSlug },
        replace: true,
      });
    }
    if (kit.type === "template") {
      return navigate({
        to: "/w/$groupSlug/settings/templates",
        params: { groupSlug },
        replace: true,
      });
    }

    return navigate({
      to: "/w/$groupSlug",
      params: { groupSlug },
      replace: true,
    });
  };

  if (!kit) {
    return null;
  }

  const canDeleteProject =
    kit.type !== "project" ||
    (kit.user_uuid === authDetails?.uuid && kit.group_uuid === groupUuid);

  if (!canDeleteProject) {
    return null;
  }

  const label = kitLabel(kit);

  return (
    <>
      <Page>
        <PageHeader>
          <PageHeader.Title>
            Delete {label}: {kit.name}
          </PageHeader.Title>
        </PageHeader>
        <PageBody>
          <KitDeletionPreviewContent
            kit={kit}
            deletionPreview={deletionPreview}
          />
          <Separator className="mb-8" />
          <div className="flex justify-end">
            <Button
              variant="destructive"
              disabled={isLoading || isDeletingKit}
              onClick={() => setOpenDeleteDialog(true)}
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete {label}{" "}
              {countDeletions + countDeactivations > 0 && (
                <>
                  &{" "}
                  {countDeletions > 0 && countDeactivations > 0
                    ? "Delete and Deactivate"
                    : countDeletions > 0
                      ? "Delete"
                      : "Deactivate"}{" "}
                  {countDeletions + countDeactivations > 1
                    ? "Artifacts"
                    : "Artifact"}
                </>
              )}
            </Button>
          </div>
        </PageBody>
      </Page>
      <DeleteDialog
        kit={kit}
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
        onDelete={handleDelete}
        isDeletingKit={isDeletingKit}
      />
    </>
  );
};

const DeleteDialog = ({
  kit,
  open,
  onOpenChange,
  onDelete,
  isDeletingKit,
}: {
  kit: GetKitDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => void;
  isDeletingKit: boolean;
}) => {
  const label = kitLabel(kit);
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>Delete {kit.name}?</AlertDialogHeader>
        <AlertDialogDescription>
          Are you sure you want to delete this {label}?
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeletingKit}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={onDelete}
            size="sm"
            disabled={isDeletingKit}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete {label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
